// src/features/userManagement/components/ScreenPermissionSelector.jsx
//
// Per-user screen permission editor. Renders every screen with a Read and a
// Write checkbox (Write implies Read). The `value` prop is a map of
// screenId -> 'READ' | 'READ_WRITE'; a screen absent from the map is not granted.

import React, { useMemo } from 'react';

export const ACCESS_READ = 'READ';
export const ACCESS_READ_WRITE = 'READ_WRITE';

export default function ScreenPermissionSelector({
  allScreens = [],
  value = {},
  onChange,
  loading = false,
  disabled = false,
}) {
  const screens = useMemo(
    () => [...allScreens].sort((a, b) => (a.screen_name || '').localeCompare(b.screen_name || '')),
    [allScreens]
  );

  const grantedCount = Object.keys(value).length;

  const setAccess = (screenId, access) => {
    if (disabled) return;
    const next = { ...value };
    if (access == null) delete next[screenId];
    else next[screenId] = access;
    onChange?.(next);
  };

  // Read toggle: on = grant read-only; off = revoke the screen entirely.
  const toggleRead = (screenId) =>
    setAccess(screenId, value[screenId] ? null : ACCESS_READ);

  // Write toggle: on = grant read/write (implies read); off = downgrade to read-only.
  const toggleWrite = (screenId) =>
    setAccess(screenId, value[screenId] === ACCESS_READ_WRITE ? ACCESS_READ : ACCESS_READ_WRITE);

  const bulk = (mode) => {
    if (disabled) return;
    const next = {};
    if (mode !== 'clear') {
      screens.forEach((s) => {
        next[s.id] = mode === 'write' ? ACCESS_READ_WRITE : ACCESS_READ;
      });
    }
    onChange?.(next);
  };

  return (
    <div className="rounded-lg border border-gray-200">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
        <span className="text-sm font-medium text-gray-700">
          Screen Access <span className="text-gray-400 font-normal">({grantedCount} granted)</span>
        </span>
        <div className="flex gap-2">
          <button type="button" disabled={disabled} onClick={() => bulk('read')}
            className="text-xs text-blue-600 hover:underline disabled:opacity-40">All read</button>
          <button type="button" disabled={disabled} onClick={() => bulk('write')}
            className="text-xs text-blue-600 hover:underline disabled:opacity-40">All write</button>
          <button type="button" disabled={disabled} onClick={() => bulk('clear')}
            className="text-xs text-gray-500 hover:underline disabled:opacity-40">Clear</button>
        </div>
      </div>

      {loading ? (
        <div className="px-3 py-6 text-center text-sm text-gray-400">Loading screens…</div>
      ) : screens.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-gray-400">No screens available.</div>
      ) : (
        <div className="max-h-64 overflow-y-auto">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 border-b border-gray-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
            <span>Screen</span>
            <span className="w-12 text-center">Read</span>
            <span className="w-12 text-center">Write</span>
          </div>
          {screens.map((s) => {
            const access = value[s.id];
            const isRead = Boolean(access);
            const isWrite = access === ACCESS_READ_WRITE;
            return (
              <div key={s.id}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 px-3 py-2 hover:bg-gray-50">
                <span className="truncate text-sm text-gray-700" title={s.screen_url}>{s.screen_name}</span>
                <input type="checkbox" checked={isRead} disabled={disabled}
                  onChange={() => toggleRead(s.id)}
                  className="mx-auto h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <input type="checkbox" checked={isWrite} disabled={disabled}
                  onChange={() => toggleWrite(s.id)}
                  className="mx-auto h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
